import { Component } from '@theme/component';

/**
 * @typedef {Object} SpinProjectVideosRefs
 * @property {HTMLElement} grid - The card grid (event-delegation root)
 * @property {HTMLButtonElement} loadMore - Load more button
 * @property {HTMLDialogElement} dialog - Lightbox dialog
 * @property {HTMLElement} frame - Container the iframe is injected into
 */

/**
 * One category's project-video grid with "Load more" and a click-to-play lightbox.
 * Lives inside a spin-tab panel; the parent spin-tabs handles tab switching.
 * Cards are server-rendered facades (snippets/spin-project-video-cards.liquid);
 * the Wistia iframe is created only when a card is clicked.
 *
 * @extends {Component<SpinProjectVideosRefs>}
 */
class SpinProjectVideos extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.initial = parseInt(this.dataset.initial, 10) || 12;
    this.items = Array.from(this.querySelectorAll('.spin-project-videos__item'));
    this.visible = this.initial;

    this.#update();

    this.refs.grid?.addEventListener('click', this.#handleGridClick);
    this.refs.dialog?.addEventListener('close', this.#handleDialogClose);
    this.refs.dialog?.addEventListener('click', this.#handleBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.grid?.removeEventListener('click', this.#handleGridClick);
    this.refs.dialog?.removeEventListener('close', this.#handleDialogClose);
    this.refs.dialog?.removeEventListener('click', this.#handleBackdropClick);
  }

  loadMore() {
    this.visible += this.initial;
    this.#update();
  }

  closeDialog() {
    this.refs.dialog?.close();
  }

  #update() {
    this.items.forEach((item, index) => {
      item.hidden = index >= this.visible;
    });

    if (this.refs.loadMore) {
      this.refs.loadMore.hidden = this.visible >= this.items.length;
    }
  }

  #handleGridClick = (event) => {
    const card = event.target.closest('.spin-project-videos__card');
    if (!card) return;

    this.#openVideo(card.dataset.embed, card.getAttribute('aria-label'));
  };

  #openVideo(embed, label) {
    if (!embed || !this.refs.dialog || !this.refs.frame) return;

    // Show the loading spinner immediately — the Wistia iframe takes a moment.
    if (this.refs.loader) this.refs.loader.hidden = false;

    const separator = embed.includes('?') ? '&' : '?';
    const iframe = document.createElement('iframe');
    iframe.src = `${embed}${separator}autoPlay=true`;
    iframe.title = label ?? 'Video';
    iframe.allow = 'autoplay; fullscreen';
    iframe.allowFullscreen = true;
    iframe.addEventListener('load', () => {
      if (this.refs.loader) this.refs.loader.hidden = true;
    }, { once: true });

    // Append (don't replace) so the persistent loader element survives.
    this.refs.frame.appendChild(iframe);
    this.refs.dialog.showModal();
  }

  #handleDialogClose = () => {
    // Remove the iframe (stops playback) but keep the loader element; reset it.
    this.refs.frame?.querySelector('iframe')?.remove();
    if (this.refs.loader) this.refs.loader.hidden = true;
  };

  // Close when the backdrop (the dialog element itself) is clicked.
  #handleBackdropClick = (event) => {
    if (event.target === this.refs.dialog) {
      this.refs.dialog.close();
    }
  };
}

if (!customElements.get('spin-project-videos')) {
  customElements.define('spin-project-videos', SpinProjectVideos);
}
